import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import Sidebar from '../../components/sidebar/Sidebar';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import DashboardHeader from '../../components/dashboardHeader/DashboardHeader';
import DashboardFooter from '../../components/dashboardFooter/DashboardFooter';
import { useUser } from '../../UserContext';
import Settings from './Settings/Settings';
import Friends from './Friends/Friends';
import Download from './Download/Download';
import Clubs from './Clubs/Clubs';
import ClubView from './Clubs/ClubView';
import ScoreShoot from './Score/ScoreShoot';
import FunShootScoring from './Score/FunShootScoring';
import ClayScoreSummary from './Score/ClayScoreSummary';
import DashboardScore from './Score/DashboardScore';
import ClayScore from './Score/ClayScore';
import RifleScore from './Score/RifleScore';
import GameScore from './Score/GameScore';
import RifleScoreSheetDetail from './Score/RifleScoreSheetDetail';
import RifleZeroDetail from './Score/RifleZeroDetail';
import TrainingSessionDetail from './Score/TrainingSessionDetail';
import GameDayDetail from './Score/GameDayDetail';
import Analytics from './Analytics/Analytics';
import GunCabinet from './GunCabinet/GunCabinet';
import Subscription from './Subscription/Subscription';
import SubscriptionSuccess from './Subscription/SubscriptionSuccess';
import SubscriptionCancel from './Subscription/SubscriptionCancel';
import Equipment from './Equipment/Equipment';
import ProfileView from './ProfileView/ProfileView';
import DashboardHomePage from './Home/DashboardHomePage';
import ManageClubs from './ManageClubs/ManageClubs';
import ClubDashboard from './ManageClubs/ClubDashboard';
import ManageMembers from './ManageClubs/ManageMembers';
import ManageStands from './ManageClubs/ManageStands';
import CourseBuilder from './ManageClubs/CourseBuilder';
import AddStand from './ManageClubs/AddStand';
import EditStand from './ManageClubs/EditStand';
import EditClub from './ManageClubs/EditClub';
import ManageEvents from './ManageClubs/ManageEvents';
import ManageMedia from './ManageClubs/ManageMedia';
import ManageOffers from './ManageClubs/ManageOffers';
import VenuePlan from './ManageClubs/VenuePlan';
import VenueStorefront from './ManageClubs/VenueStorefront';
import ManageCommunications from './ManageClubs/ManageCommunications';
import ClubTill from './ManageClubs/ClubTill';
import AdminDashboard from './Admin/AdminDashboard';
import AdminBanners from './Admin/AdminBanners';
import AdminUsers from './Admin/AdminUsers';
import AdminEquipmentData from './Admin/AdminEquipmentData';
import AdminPerks from './Admin/AdminPerks';
import AdminMarketplace from './Admin/AdminMarketplace';
import Blog from './Blog/Blog';
import BlogPost from './Blog/BlogPost';
import BlogEditor from './Blog/BlogEditor';
import BlogManager from './Blog/BlogManager';
import Marketplace from './Marketplace/Marketplace';
import ListingDetail from './Marketplace/ListingDetail';
import CreateListing from './Marketplace/CreateListing';
import MyListings from './Marketplace/MyListings';
import VenueMarketplaceHome from './Marketplace/VenueMarketplaceHome';
import ConnectPerks from '../../pages/Perks/ConnectPerks';

const ProtectedAdminRoute = ({ canAccess, children }) => {
    if (!canAccess) {
        return <Navigate to="/dashboard/home" replace />;
    }
    return children;
};

const ProtectedVenueManagerRoute = ({ canAccess, children }) => {
    if (!canAccess) {
        return <Navigate to="/dashboard/home" replace />;
    }
    return children;
};

const Dashboard = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();
    const { user } = useUser();
    const canAccessAdmin = Boolean(user?.isAdmin);
    const canAccessVenueManager = Boolean(user?.isAdmin || (user?.clubAdminCount || 0) > 0);

    // Close sidebar when route changes
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
    };

    return (
        <div className='dashboard-page'>
            <Sidebar toggleSidebar={toggleSidebar} closeSidebar={closeSidebar} opened={sidebarOpen} />
            <div className="dashboard-page-content">
                <DashboardHeader
                    toggleSidebar={toggleSidebar}
                    opened={sidebarOpen}
                />
                <Routes key={location.pathname}> {/* Add key to force re-render */}
                    <Route path="home" element={<DashboardHomePage />} />
                    <Route path="clubs" element={<Clubs />} />
                    <Route path="clubs/:clubId" element={<ClubView />} />
                    <Route path="score" element={<DashboardScore />} />
                    <Route path="score/clay" element={<ClayScore />} />
                    <Route path="score/rifle" element={<RifleScore />} />
                    <Route path="score/game" element={<GameScore />} />
                    <Route path="score/rifle/:id" element={<RifleScoreSheetDetail />} />
                    <Route path="score/zero/:id" element={<RifleZeroDetail />} />
                    <Route path="score/training/:id" element={<TrainingSessionDetail />} />
                    <Route path="score/game/:id" element={<GameDayDetail />} />
                    <Route path="score/score-shoot/:scoreSheetId" element={<ScoreShoot />} />
                    <Route path="score/fun-shoot/:scoreSheetId" element={<FunShootScoring />} />
                    <Route path="score/summary/:scoreSheetId" element={<ClayScoreSummary />} />
                    <Route path="analytics" element={<Analytics />} />
                    <Route path="gun-cabinet" element={<GunCabinet />} />
                    <Route path="friends" element={<Friends />} />
                    <Route path="friends/:friendUsername" element={<ProfileView />} />
                    <Route path="download" element={<Download />} />
                    <Route path="setting" element={<Settings />} />
                    <Route path="subscription" element={<Subscription />} />
                    <Route path="subscriptionSuccess" element={<SubscriptionSuccess />} />
                    <Route path="subscriptionCancel" element={<SubscriptionCancel />} />
                    <Route path="equipment" element={<Equipment />} />
                    <Route path="manage-clubs" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><ManageClubs /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/dashboard" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><ClubDashboard /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/edit" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><EditClub /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/members" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><ManageMembers /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/stands" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><ManageStands /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/stands/course-builder" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><CourseBuilder /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/stands/add" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><AddStand /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/stands/edit/:standId" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><EditStand /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/events" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><ManageEvents /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/media" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><ManageMedia /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/offers" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><ManageOffers /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/storefront" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><VenueStorefront /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/plan" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><VenuePlan /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/till" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><ClubTill /></ProtectedVenueManagerRoute>} />
                    <Route path="club/:clubId/communications" element={<ProtectedVenueManagerRoute canAccess={canAccessVenueManager}><ManageCommunications /></ProtectedVenueManagerRoute>} />
                    <Route path="admin" element={<ProtectedAdminRoute canAccess={canAccessAdmin}><AdminDashboard /></ProtectedAdminRoute>} />
                    <Route path="admin/banners" element={<ProtectedAdminRoute canAccess={canAccessAdmin}><AdminBanners /></ProtectedAdminRoute>} />
                    <Route path="admin/users" element={<ProtectedAdminRoute canAccess={canAccessAdmin}><AdminUsers /></ProtectedAdminRoute>} />
                    <Route path="admin/blog" element={<ProtectedAdminRoute canAccess={canAccessAdmin}><BlogManager /></ProtectedAdminRoute>} />
                    <Route path="admin/equipment" element={<ProtectedAdminRoute canAccess={canAccessAdmin}><AdminEquipmentData /></ProtectedAdminRoute>} />
                    <Route path="admin/perks" element={<ProtectedAdminRoute canAccess={canAccessAdmin}><AdminPerks /></ProtectedAdminRoute>} />
                    <Route path="admin/marketplace" element={<ProtectedAdminRoute canAccess={canAccessAdmin}><AdminMarketplace /></ProtectedAdminRoute>} />
                    <Route path="blog" element={<Blog />} />
                    <Route path="blog/:slug" element={<BlogPost />} />
                    <Route path="blog/editor" element={<BlogEditor />} />
                    <Route path="blog/editor/:id" element={<BlogEditor />} />
                    <Route path="marketplace" element={<Marketplace />} />
                    <Route path="marketplace/my-listings" element={<MyListings />} />
                    <Route path="marketplace/create" element={<CreateListing />} />
                    <Route path="marketplace/venue/:clubId" element={<VenueMarketplaceHome />} />
                    <Route path="marketplace/:id" element={<ListingDetail />} />
                    <Route path="connect-perks" element={<ConnectPerks />} />
                    <Route path="*" element={<DashboardHomePage />} /> {/* Fallback */}
                </Routes>
                <DashboardFooter />
            </div>
        </div>
    );
};

export default Dashboard;
